import type {
  ApprovalCallbackPayload,
  FileManagerItem,
  FileManagerQueryContext,
} from "@/components/core/FileManager/FileManager.types";
import type { DocumentActionPayload } from "@/api/documents/documents.types";
import {
  APPROVED_FOLDER_NAME,
  buildOriginTag,
  isSystemTag,
  SYSTEM_TAG_APPROVED,
} from "./fileTags.utils";

type BrowseFn = (parentId: string | null, search: string | null) => Promise<FileManagerItem[]>;
type UpdateFn = (payload: DocumentActionPayload) => Promise<unknown>;

export async function approveFiles(
  payload: ApprovalCallbackPayload,
  ctx: FileManagerQueryContext,
  browseItems: BrowseFn,
  updateDocuments: UpdateFn,
): Promise<{ successMessage: string }> {
  // Temporary comment: TODO Kennedy - move to backend
  const isCopy = payload.action === "copy";

  const [rootItems, sourceFolderItems] = await Promise.all([
    browseItems(null, null),
    isCopy && ctx.currentFolderId ? browseItems(ctx.currentFolderId, null) : Promise.resolve([]),
  ]);

  const targetFolder = rootItems.find(
    (item) => item.type === "FOLDER" && item.name === APPROVED_FOLDER_NAME,
  );
  if (!targetFolder) throw new Error(`"${APPROVED_FOLDER_NAME}" folder was not found.`);

  const existingApproved = await browseItems(targetFolder.id, null);
  const beforeIds = new Set(existingApproved.map((f) => f.id));

  const sourceFolderName = ctx.breadcrumb.at(-1)?.name ?? null;
  const sourceFolderId = ctx.currentFolderId;

  await updateDocuments({
    action: isCopy ? "copyItems" : "moveItems",
    itemIds: payload.items.map((i) => i.id),
    destinationParentId: targetFolder.id,
  });

  if (sourceFolderId && sourceFolderName) {
    const originTag = buildOriginTag(sourceFolderName);
    const approvedFiles = await browseItems(targetFolder.id, null);
    const newFiles = approvedFiles.filter((f) => !beforeIds.has(f.id));

    const tagPromises: Promise<unknown>[] = payload.items.flatMap((item) => {
      const destFile = newFiles.find((f) => f.name === item.name);
      const tasks: Promise<unknown>[] = [];

      if (destFile) {
        const baseTags = destFile.tags.map((t) => t.name).filter((n) => !isSystemTag(n));
        tasks.push(
          updateDocuments({
            action: "updateTags",
            itemId: destFile.id,
            tags: [...baseTags, originTag, SYSTEM_TAG_APPROVED],
          }),
        );
      }

      if (isCopy) {
        const sourceFile = sourceFolderItems.find((f) => f.id === item.id);
        if (sourceFile) {
          const baseTags = sourceFile.tags.map((t) => t.name).filter((n) => !isSystemTag(n));
          tasks.push(
            updateDocuments({
              action: "updateTags",
              itemId: item.id,
              tags: [...baseTags, SYSTEM_TAG_APPROVED],
            }),
          );
        }
      }

      return tasks;
    });

    await Promise.all(tagPromises);
  }

  const count = payload.items.length;
  const noun = `file${count !== 1 ? "s" : ""}`;
  return {
    successMessage: `${count} ${noun} ${isCopy ? "copied" : "moved"} to "${APPROVED_FOLDER_NAME}"`,
  };
}
